import { Injectable } from '@angular/core';
import { ErrorUtilities, isDefined, NotificationIdentifier } from '@evorait/pace-core';
import { ContentResults } from '../types/content-results';
import { FormTypeFilters } from '../types/form-type-filters';
import { FormsConstants } from '../types/forms-constants';
import { FUNCTIONAL_ELEMENT_TYPE } from '../types/functional-element';
import { AstoriaXmlUtilities } from '../utilities/astoria-xml-utilities';
import { CosimaXmlUtilities } from '../utilities/cosima-xml-utilities';
import { FormUtilities } from '../utilities/form-utilities';
import { checkForErrorDocument } from '../utilities/xml-dom-utilities';
import { XmlSerializerService } from './xml-serializer.service';

@Injectable({
    providedIn: 'root',
})
export class XmlParserService {
    private internalXmlDoc: XMLDocument | undefined;

    // We serialize the XML after each successful change of the XML document to keep the store state synced to the XML
    // document. To avoid creating a new XMLSerializer each time, we store it as instance variable.
    private readonly serializer = new XMLSerializer();
    public constructor(private readonly xmlSerializerService: XmlSerializerService) {}

    public setXml(xml: string): boolean {
        const parser = new DOMParser();
        const doc = parser.parseFromString(xml, 'text/xml');
        const isValidXml = checkForErrorDocument(doc);
        if (isValidXml) {
            this.xmlDoc = doc;
        } else {
            throw new Error(`XmlParserService: setXml: Could not parse XML!`);
        }

        return isValidXml;
    }

    public set xmlDoc(document: XMLDocument | undefined) {
        this.internalXmlDoc = document;
        if (!isDefined(document)) {
            this.xmlSerializerService.unsetXml();
        } else {
            this.xmlSerializerService.setXml(document);
        }
    }

    public get xmlDoc(): XMLDocument | undefined {
        return this.internalXmlDoc;
    }

    public getXml(doctype: string | undefined): string {
        if (typeof this.xmlDoc === 'undefined') {
            throw new Error(`XmlParserService: getXml: xmlDoc is undefined!`);
        }

        let xml = this.serializer.serializeToString(this.xmlDoc);

        if (isDefined(doctype)) {
            // Replace doctype with original doctype, otherwise the entity definitions will be lost
            xml = xml.replace(/<!DOCTYPE.*?>/gi, doctype);
        }

        return xml;
    }

    public getContent(
        doctype: string | undefined,
        type: FormsConstants.FORMTYPE,
        typeFilters: FormTypeFilters,
        notificationId: NotificationIdentifier,
        supportsParticipantCopies: boolean,
        hasLock: boolean,
        saveMode: FormsConstants.LOAD_STATUS,
    ): ContentResults {
        if (typeof this.xmlDoc === 'undefined') {
            throw new Error(`XmlParserService: getContent: XML Doc is undefined`);
        }

        let content: ContentResults | undefined;

        try {
            if (FormUtilities.isAstoriaForm(type)) {
                if (!isDefined(doctype)) {
                    throw new Error(
                        `XmlParserService: getContent: Cannot extract content from Astoria XML without doctype!`,
                    );
                }
                content = AstoriaXmlUtilities.getContent(
                    this.xmlDoc,
                    doctype,
                    type,
                    typeFilters,
                    supportsParticipantCopies,
                );
            }
            if (FormUtilities.isCosimaForm(type)) {
                if (isDefined(doctype)) {
                    throw new Error(
                        `XmlParserService: getContent: Cannot extract content from Cosima XML with doctype!`,
                    );
                }
                content = CosimaXmlUtilities.getContent(this.xmlDoc, type, typeFilters);
            }
        } catch (error) {
            const errorMessage = ErrorUtilities.getErrorMessageFromStack(
                JSON.stringify(error, Object.getOwnPropertyNames(error)),
            );
            throw new Error(`${errorMessage}${hasLock ? FormsConstants.HAS_LOCK : ''}`);
        }

        if (!isDefined(content)) {
            throw new Error(`XmlParserService: getContent: Content is undefined, but no parsing error was thrown?`);
        }

        // Run some sanity checks on the returned content, eg. if there is both a customer and an employee signature
        // where required by the participant copy workflow
        if (
            supportsParticipantCopies &&
            (type === FormsConstants.FORMTYPE.MAINTENANCE || type === FormsConstants.FORMTYPE.SAFETY)
        ) {
            const employeeSignature = Object.values(content.functionalElements).find(
                (f) => f.type === FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_EMPLOYEE,
            );
            if (typeof employeeSignature === 'undefined') {
                throw new Error(
                    `XmlParserService: getContent: Could not find employee signature for training checklist!${
                        hasLock ? FormsConstants.HAS_LOCK : ''
                    }`,
                );
            }
            const customerSignature = Object.values(content.functionalElements).find(
                (f) => f.type === FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_CUSTOMER,
            );
            if (typeof customerSignature === 'undefined') {
                throw new Error(
                    `XmlParserService: getContent: Could not find customer signature for training checklist!${
                        hasLock ? FormsConstants.HAS_LOCK : ''
                    }`,
                );
            }
        }

        const customerIdentification =
            saveMode === FormsConstants.LOAD_STATUS.INTERIM &&
            // SHP-6309: Comparison with serial number required to fix interims broken by INC3733042
            content.customerIdentification.serialNr.value !== '---'
                ? this.xmlSerializerService.updateOrderNumberInCustomerIdentification(
                      content.customerIdentification,
                      notificationId,
                  )
                : this.xmlSerializerService.updateCustomerIdentification(
                      content.customerIdentification,
                      notificationId,
                      type,
                  );

        return {
            ...content,
            customerIdentification,
        };
    }

    /**
     * Only relevant for Astoria XML. There the doctype contains the entity references for the signature
     * image date. Cosima does not include a doctype definition in its files as signatures reference their
     * image files using a href attribute instead.
     *
     * @param xml The XML string to extract the doctype from.
     * @returns The doctype string, including the opening and closing tags.
     */
    public static getDoctype(xml: string): string {
        const openingIndex = xml.indexOf('<!');
        const closingIndexAdditionalPosition = 2;
        let closingIndex = xml.indexOf(']>', openingIndex);
        // Check if the DOCTYPE was actually closed by ']>'; otherwise use just '>'
        // Background: If the form contains signatures, it will also contain a list of entities in the DOCTYPE tag. This
        //             list of entities will be closed with a ']'; then the closing '>' for the DOCTYPE tag follows
        //             (resulting in a ']>' sequence). This is for maintenance and safety forms.
        //             If there are no signatures, there is no list of entities and the doctype tag will simply be closed
        //             with a '>'. This is for training checklist forms.
        if (closingIndex <= openingIndex) {
            closingIndex = xml.indexOf('>', openingIndex) + 1;
        } else {
            closingIndex += closingIndexAdditionalPosition;
        }

        return xml.substring(openingIndex, closingIndex);
    }
}
